import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  MapPin,
  Pencil,
  Trash2,
  Users,
  Video,
} from 'lucide-react'
import { apiJson } from '../api'
import { hasAnyRole } from '../auth'

export interface Session {
  id: string
  courseId: string
  courseName: string
  title: string
  format: 'classroom' | 'webinar'
  startAt: string
  endAt: string
  location?: string | null
  meetingUrl?: string | null
  capacity: number
  instructorName?: string | null
  status: 'scheduled' | 'cancelled' | 'completed'
  registeredCount: number
  seatsRemaining: number | null
  myStatus: 'registered' | 'attended' | null
}

interface Attendee {
  id: string
  username: string
  employeeName?: string | null
  status: 'registered' | 'attended' | 'cancelled'
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

function isoDay(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function fmtTime(iso: string) {
  const d = new Date(iso)
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

const sessionStatusMeta: Record<string, { label: string; cls: string }> = {
  scheduled: { label: 'Scheduled', cls: 'bg-sky-50 text-sky-700 ring-sky-100' },
  cancelled: { label: 'Cancelled', cls: 'bg-red-50 text-red-600 ring-red-100' },
  completed: { label: 'Completed', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100' },
}

export default function LearningCalendar() {
  const navigate = useNavigate()
  const allowManage = hasAnyRole('admin', 'hr')
  const isAdmin = hasAnyRole('admin')

  const today = new Date()
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth()) // 0-based
  const [sessions, setSessions] = useState<Session[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selectedDay, setSelectedDay] = useState<string>(isoDay(today))
  const [actingId, setActingId] = useState<string | null>(null)

  // attendance panel state
  const [attendanceFor, setAttendanceFor] = useState<string | null>(null)
  const [attendees, setAttendees] = useState<Attendee[]>([])
  const [attendanceMarks, setAttendanceMarks] = useState<Record<string, boolean>>({})
  const [savingAttendance, setSavingAttendance] = useState(false)

  const range = useMemo(() => {
    const from = new Date(year, month, 1)
    const to = new Date(year, month + 1, 1)
    return { from: isoDay(from), to: isoDay(to) }
  }, [year, month])

  const fetchSessions = useCallback(async () => {
    setLoading(true)
    try {
      setSessions(await apiJson<Session[]>(`/sessions?from=${range.from}&to=${range.to}`))
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sessions')
    } finally {
      setLoading(false)
    }
  }, [range])

  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  const byDay = useMemo(() => {
    const map = new Map<string, Session[]>()
    for (const s of sessions ?? []) {
      const key = isoDay(new Date(s.startAt))
      map.set(key, [...(map.get(key) ?? []), s])
    }
    return map
  }, [sessions])

  // build the 6x7 grid (Monday-first)
  const cells = useMemo(() => {
    const first = new Date(year, month, 1)
    const startOffset = (first.getDay() + 6) % 7 // Mon=0
    const start = new Date(year, month, 1 - startOffset)
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
      return { date: d, key: isoDay(d), inMonth: d.getMonth() === month }
    })
  }, [year, month])

  const daySessions = byDay.get(selectedDay) ?? []

  const prevMonth = () => {
    if (month === 0) { setYear(year - 1); setMonth(11) } else setMonth(month - 1)
  }
  const nextMonth = () => {
    if (month === 11) { setYear(year + 1); setMonth(0) } else setMonth(month + 1)
  }

  const register = async (s: Session) => {
    setActingId(s.id)
    setError('')
    try {
      await apiJson(`/sessions/${s.id}/register`, { method: 'POST' })
      await fetchSessions()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed')
    } finally {
      setActingId(null)
    }
  }

  const unregister = async (s: Session) => {
    setActingId(s.id)
    setError('')
    try {
      await apiJson(`/sessions/${s.id}/unregister`, { method: 'POST' })
      await fetchSessions()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cancel failed')
    } finally {
      setActingId(null)
    }
  }

  const removeSession = async (s: Session) => {
    if (!window.confirm(`Delete session “${s.title}” and all RSVPs?`)) return
    setActingId(s.id)
    setError('')
    try {
      await apiJson(`/sessions/${s.id}`, { method: 'DELETE' })
      await fetchSessions()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setActingId(null)
    }
  }

  const openAttendance = async (s: Session) => {
    setError('')
    try {
      const detail = await apiJson<{ attendees?: Attendee[] }>(`/sessions/${s.id}`)
      const list = (detail.attendees ?? []).filter((a) => a.status !== 'cancelled')
      setAttendees(list)
      setAttendanceMarks(Object.fromEntries(list.map((a) => [a.username, a.status === 'attended'])))
      setAttendanceFor(s.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load attendees')
    }
  }

  const saveAttendance = async (s: Session) => {
    setSavingAttendance(true)
    setError('')
    try {
      await apiJson(`/sessions/${s.id}/attendance`, {
        method: 'POST',
        body: JSON.stringify({
          marks: Object.entries(attendanceMarks).map(([username, attended]) => ({ username, attended })),
        }),
        headers: { 'Content-Type': 'application/json' },
      })
      setAttendanceFor(null)
      await fetchSessions()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSavingAttendance(false)
    }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-5">
      {/* month grid */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm lg:col-span-3">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <button onClick={prevMonth} className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
            <ChevronLeft size={16} />
          </button>
          <p className="flex items-center gap-2 text-sm font-bold tracking-tight">
            <CalendarDays size={15} className="text-brand-600" />
            {MONTHS[month]} {year}
          </p>
          <button onClick={nextMonth} className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
            <ChevronRight size={16} />
          </button>
        </div>
        <div className={`transition-opacity ${loading ? 'opacity-60' : ''}`}>
          <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50/60">
            {WEEKDAYS.map((d) => (
              <div key={d} className="px-2 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-slate-400">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((c) => {
              const dayList = byDay.get(c.key) ?? []
              const isToday = c.key === isoDay(new Date())
              const isSelected = c.key === selectedDay
              return (
                <button
                  key={c.key}
                  onClick={() => setSelectedDay(c.key)}
                  className={`min-h-[72px] border-b border-r border-slate-100 p-1.5 text-left align-top transition-colors [&:nth-child(7n)]:border-r-0 ${
                    isSelected ? 'bg-brand-50/70' : 'hover:bg-slate-50'
                  }`}
                >
                  <span
                    className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                      isToday
                        ? 'bg-brand-600 text-white'
                        : c.inMonth
                          ? 'text-slate-700'
                          : 'text-slate-300'
                    }`}
                  >
                    {c.date.getDate()}
                  </span>
                  <div className="mt-1 space-y-0.5">
                    {dayList.slice(0, 2).map((s) => (
                      <div
                        key={s.id}
                        className={`truncate rounded px-1 py-0.5 text-[10px] font-semibold ${
                          s.status === 'cancelled'
                            ? 'bg-red-50 text-red-500 line-through'
                            : s.myStatus
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-brand-100 text-brand-800'
                        }`}
                      >
                        {fmtTime(s.startAt)} {s.courseName}
                      </div>
                    ))}
                    {dayList.length > 2 && (
                      <p className="px-1 text-[10px] font-medium text-slate-400">+{dayList.length - 2} more</p>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* day detail */}
      <div className="space-y-3 lg:col-span-2">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
          {new Date(`${selectedDay}T00:00:00`).toLocaleDateString('en-GB', {
            weekday: 'long', day: 'numeric', month: 'long',
          })}
        </p>

        {error && (
          <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle size={16} className="shrink-0" /> {error}
          </div>
        )}

        {daySessions.length === 0 ? (
          <div className="flex flex-col items-center rounded-2xl border border-slate-200 bg-white py-12 text-center shadow-sm">
            <CalendarDays size={22} className="text-slate-300" />
            <p className="mt-2 text-sm font-semibold">No sessions</p>
            <p className="mt-0.5 text-xs text-slate-400">Nothing scheduled for this day.</p>
          </div>
        ) : (
          daySessions.map((s) => {
            const st = sessionStatusMeta[s.status]
            const full = s.seatsRemaining !== null && s.seatsRemaining <= 0
            const started = new Date(s.startAt) <= new Date()
            return (
              <div key={s.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <button onClick={() => navigate(`/courses/${s.courseId}`)} className="text-left text-sm font-bold tracking-tight hover:text-brand-700">
                      {s.courseName}
                    </button>
                    <p className="mt-0.5 truncate text-xs text-slate-500">{s.title}</p>
                  </div>
                  <span className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${st.cls}`}>
                    {st.label}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <Clock size={11} /> {fmtTime(s.startAt)} – {fmtTime(s.endAt)}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    {s.format === 'webinar' ? <Video size={11} /> : <MapPin size={11} />}
                    {s.format === 'webinar' ? 'Webinar' : s.location ?? 'Classroom'}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Users size={11} /> {s.registeredCount}{s.capacity > 0 ? `/${s.capacity}` : ''}
                  </span>
                  {s.instructorName && <span>· {s.instructorName}</span>}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {s.myStatus ? (
                    <>
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-100">
                        <CheckCircle2 size={11} /> {s.myStatus === 'attended' ? 'Attended' : 'Registered'}
                      </span>
                      {s.myStatus === 'registered' && (
                        <button
                          onClick={() => unregister(s)}
                          disabled={actingId === s.id}
                          className="rounded-lg px-2.5 py-1 text-[11px] font-semibold text-slate-500 transition-colors hover:bg-slate-100 disabled:opacity-40"
                        >
                          Cancel RSVP
                        </button>
                      )}
                    </>
                  ) : s.status === 'scheduled' && !started ? (
                    <button
                      onClick={() => register(s)}
                      disabled={actingId === s.id || full}
                      className="rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-3.5 py-1.5 text-[11px] font-semibold text-white shadow-md shadow-brand-500/25 transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {actingId === s.id ? 'Registering…' : full ? 'Session full' : 'Register'}
                    </button>
                  ) : null}
                  {s.format === 'webinar' && s.meetingUrl && s.myStatus && (
                    <a
                      href={s.meetingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-semibold text-brand-600 transition-colors hover:bg-brand-50"
                    >
                      <ExternalLink size={11} /> Join meeting
                    </a>
                  )}
                  {allowManage && (
                    <span className="ml-auto inline-flex items-center gap-1">
                      <button
                        onClick={() => openAttendance(s)}
                        className="rounded-lg px-2.5 py-1 text-[11px] font-semibold text-slate-500 transition-colors hover:bg-slate-100"
                      >
                        Attendance
                      </button>
                      <button
                        onClick={() => navigate(`/sessions/${s.id}/edit`)}
                        className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-brand-50 hover:text-brand-600"
                        title="Edit session"
                      >
                        <Pencil size={13} />
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => removeSession(s)}
                          disabled={actingId === s.id}
                          className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                          title="Delete session"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </span>
                  )}
                </div>

                {/* attendance panel */}
                {attendanceFor === s.id && (
                  <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                    {attendees.length === 0 ? (
                      <p className="py-2 text-center text-xs text-slate-400">No active registrations.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {attendees.map((a) => (
                          <label key={a.id} className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-600">
                            <input
                              type="checkbox"
                              checked={attendanceMarks[a.username] ?? false}
                              onChange={(e) =>
                                setAttendanceMarks({ ...attendanceMarks, [a.username]: e.target.checked })
                              }
                              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-400"
                            />
                            <span className="flex-1 truncate">{a.employeeName ?? a.username}</span>
                            <span className="text-[10px] text-slate-400">{a.username}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 flex justify-end gap-2">
                      <button
                        onClick={() => setAttendanceFor(null)}
                        className="rounded-lg px-3 py-1.5 text-[11px] font-semibold text-slate-500 hover:bg-slate-100"
                      >
                        Close
                      </button>
                      {attendees.length > 0 && (
                        <button
                          onClick={() => saveAttendance(s)}
                          disabled={savingAttendance}
                          className="rounded-lg bg-gradient-to-r from-emerald-600 to-emerald-700 px-3 py-1.5 text-[11px] font-semibold text-white shadow-md shadow-emerald-500/25 hover:brightness-110 disabled:opacity-40"
                        >
                          {savingAttendance ? 'Saving…' : 'Save attendance'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
